/**
 * weldAssurePrint.js — print-faithful PDF rendering for WeldAssure WPS / PQR / WQTR
 * records, matching the iOS WeldAssure app's "Controlled Document" form layout
 * (AWS D1.1 dense-grid house style). Opens a print window and triggers the
 * browser print dialog (Save as PDF), so the printed page mirrors the app.
 *
 * Fields the web record does not yet capture render as "N/A" / blank, exactly
 * as the app shows unset fields.
 */

const esc = (s) =>
  String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const v = (x) => {
  const s = (x == null ? "" : String(x)).trim();
  return s === "" ? "N/A" : esc(s);
};

const yn = (val) => {
  const s = String(val == null ? "" : val).toLowerCase();
  const yes = ["yes", "y", "true", "1", "steel", "flux"].includes(s) || s.startsWith("y");
  return `<span class="chk">${yes ? "&#9745;" : "&#9744;"}</span> Yes&nbsp;&nbsp;<span class="chk">${yes ? "&#9744;" : "&#9745;"}</span> No`;
};

const box = (checked, label) =>
  `<span class="chk">${checked ? "&#9745;" : "&#9744;"}</span> ${esc(label)}`;

// label/value row used inside the two-column body
const row = (label, value) =>
  `<div class="r"><span class="rl">${esc(label)}</span><span class="rv">${value}</span></div>`;

const sect = (title) => `<div class="sh">${esc(title)}</div>`;

const fmtDate = (d) => {
  if (!d) return "N/A";
  const dt = new Date(d);
  if (isNaN(dt)) return esc(d);
  return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

// simple inline groove-weld sketch to evoke the app's joint detail image
const jointSketch = (caption) => `
  <div class="sketch">
    <svg viewBox="0 0 240 150" width="100%" height="120">
      <line x1="20" y1="70" x2="100" y2="70" stroke="#333" stroke-width="1.5"/>
      <line x1="20" y1="95" x2="100" y2="95" stroke="#333" stroke-width="1.5"/>
      <line x1="140" y1="70" x2="220" y2="70" stroke="#333" stroke-width="1.5"/>
      <line x1="140" y1="95" x2="220" y2="95" stroke="#333" stroke-width="1.5"/>
      <path d="M100 70 L120 95 L140 70 Z" fill="#6b3f1d" opacity="0.85"/>
      <path d="M100 70 Q120 55 140 70" fill="none" stroke="#6b3f1d" stroke-width="1"/>
      <text x="118" y="50" font-size="10" text-anchor="middle">&#945;</text>
      <text x="205" y="86" font-size="9">T1</text>
      <text x="118" y="115" font-size="9" text-anchor="middle">R</text>
    </svg>
    <div class="sketch-cap">${esc(caption || "")}</div>
  </div>`;

const CSS = `
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: Helvetica, Arial, sans-serif; color: #111; font-size: 9px; }
  .page { width: 7.7in; margin: 0 auto; padding: 0.2in 0; }
  .doc-title { text-align: center; font-weight: 700; font-size: 16px; letter-spacing: .2px; }
  .doc-sub { text-align: center; font-size: 11px; margin-top: 2px; }
  .doc-meta-right { position: absolute; top: 0.2in; right: 0; text-align: right; font-size: 9px; font-weight: 700; }
  .hdr-wrap { position: relative; margin-bottom: 8px; }
  table.meta { width: 100%; border-collapse: collapse; margin: 8px 0; }
  table.meta td { border: 0.5px solid #888; padding: 3px 5px; vertical-align: top; width: 25%; }
  .ml { font-size: 7.5px; color: #555; }
  .mv { font-weight: 700; font-size: 9.5px; }
  .cols { display: flex; gap: 10px; }
  .col { flex: 1; min-width: 0; }
  .sh { font-weight: 700; font-size: 9.5px; text-transform: uppercase; border-bottom: 1px solid #111; padding: 6px 0 2px; margin-top: 6px; }
  .r { display: flex; border-bottom: 0.5px solid #ddd; padding: 1.5px 0; }
  .rl { color: #444; width: 46%; flex-shrink: 0; }
  .rv { font-weight: 700; flex: 1; }
  .chk { font-size: 11px; }
  .sketch { border: 0.5px solid #888; padding: 4px; text-align: center; margin-top: 6px; }
  .sketch-cap { font-size: 8px; text-align: left; margin-top: 2px; }
  table.proc { width: 100%; border-collapse: collapse; margin-top: 6px; }
  table.proc th { background: #f0f0f0; border: 0.5px solid #888; padding: 3px; font-size: 7.5px; text-align: left; }
  table.proc td { border: 0.5px solid #888; padding: 5px 3px; font-size: 8.5px; vertical-align: top; }
  .proc-head { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 1px solid #111; padding-top: 8px; }
  .proc-head .t { font-weight: 700; font-size: 9.5px; text-transform: uppercase; }
  .proc-head .hi { font-weight: 700; font-size: 9px; }
  .full { border: 0.5px solid #888; padding: 6px; margin-top: 6px; min-height: 40px; white-space: pre-wrap; }
  .ftr { display: flex; justify-content: space-between; margin-top: 14px; padding-top: 6px; border-top: 0.5px solid #888; font-size: 8px; color: #555; }
  @page { size: letter; margin: 0.35in; }
  @media print { .page { width: 100%; } }
`;

function metaCell(label, value) {
  return `<td><div class="ml">${esc(label)}</div><div class="mv">${value}</div></td>`;
}

function shell({ title, subtitle, docNo, rev, body, footerLeft }) {
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(docNo)}</title>
<style>${CSS}</style></head><body onload="setTimeout(function(){window.print();},250);">
<div class="page">
  <div class="hdr-wrap">
    <div class="doc-meta-right">${esc(title.split(" ")[0])} No. ${v(docNo)}<br>Rev. ${v(rev)}</div>
    <div class="doc-title">WeldAssure</div>
    <div class="doc-sub">${esc(subtitle)}</div>
  </div>
  ${body}
  <div class="ftr">
    <div>${esc(footerLeft)}</div>
    <div>Controlled Document &ndash; Generated by WeldAssure &bull; Page 1 of 1</div>
  </div>
</div>
</body></html>`;
}

function openPrint(html) {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) { alert("Please allow pop-ups to print this document."); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export function printWPS(doc) {
  const dd = doc.documentData || {};
  const j = dd.joint || {}, bm = dd.baseMetals || {}, fl = dd.filler || {};
  const sh = dd.shielding || {}, ph = dd.preheat || {}, pos = dd.position || {};
  const el = dd.electrical || {}, tc = dd.technique || {}, pw = dd.pwht || {}, pr = dd.procedure || {};
  const awsClass = fl.awsClass || dd.fillerMetal || "";
  const gas = sh.gas || dd.shieldingGas || "";
  const bf = (val, label) => box(String(val || "") === label, label);

  const meta = `<table class="meta"><tbody>
    <tr>${metaCell("WPS No.", v(doc.wpsNumber))}${metaCell("Revision", v(doc.revision))}${metaCell("Date", fmtDate(doc.wpsDate))}${metaCell("Prepared By", v(doc.preparedBy))}</tr>
    <tr>${metaCell("Authorized By", v(doc.authorizedBy))}${metaCell("Auth. Date", fmtDate(dd.authDate))}${metaCell("Prequalified", dd.prequalified ? "Yes &ndash; AWS D1.1" : "No")}${metaCell("Supporting PQR(s)", v(dd.supportingPQRs))}</tr>
    <tr>${metaCell("Welding Process(es)", v(doc.process))}${metaCell("Type", v(dd.type))}${metaCell("AWS Joint Detail", v(dd.awsJointDetail))}${metaCell("Back Gouge", v(dd.backGouge))}</tr>
    <tr>${metaCell("Joint Type", v(j.type))}${metaCell("Backing", v(j.backing))}${metaCell("Backing Material", v(j.backingMaterial))}${metaCell("Sides", v(j.sides))}</tr>
  </tbody></table>`;

  const left = `<div class="col">
    ${sect("Joint Design")}
    ${row("Type:", v(j.type))}
    ${row("Backing:", yn(j.backing))}
    ${row("Backing Material:", v(j.backingMaterial))}
    ${row("Sides:", bf(j.sides,"Single") + "&nbsp;&nbsp;" + bf(j.sides,"Double"))}
    ${row("Root Opening:", v(j.rootOpening))}
    ${row("Root Face:", v(j.rootFace))}
    ${row("Groove Angle:", v(j.grooveAngle))}
    ${row("Radius (J-U):", v(j.radiusJU))}
    ${row("Back Gouge:", yn(dd.backGouge))}
    ${sect("Base Metals")}
    ${row("Spec. (From):", v(bm.specFrom || doc.baseMetalSpec))}
    ${row("Grade (From):", v(bm.gradeFrom))}
    ${row("Spec. (To):", v(bm.specTo))}
    ${row("Grade (To):", v(bm.gradeTo))}
    ${row("Groove Thickness:", v(bm.grooveThickness))}
    ${row("Fillet Size:", v(bm.filletSize))}
    ${row("Pipe Diameter:", v(bm.pipeDiameter))}
    ${sect("Filler Metals")}
    ${row("AWS Spec.:", v(fl.awsSpec))}
    ${row("AWS Class.:", v(awsClass))}
    ${row("SFA Spec.:", v(fl.sfaSpec))}
    ${row("Alt. Class.:", v(fl.altClass))}
    ${sect("Shielding")}
    ${row("Flux:", v(sh.flux))}
    ${row("Gas:", v(gas))}
    ${row("Composition:", v(sh.composition))}
    ${row("Flow Rate CFH:", v(sh.flowRateCFH))}
    ${row("Gas Cup Size:", v(sh.gasCupSize))}
    ${row("Electrode-Flux:", v(sh.electrodeFlux))}
    ${sect("Preheat / Interpass")}
    ${row("Preheat Min.:", v(ph.min))}
    ${row("Up to 3/4\":", v(ph.upTo34))}
    ${row("Over 3/4\" to 1-1/2\":", v(ph.over34))}
    ${row("Over 1-1/2\" to 2-1/2\":", v(ph.over112))}
    ${row("Over 2-1/2\":", v(ph.over212))}
    ${row("Interpass Min.:", v(ph.interpassMin))}
    ${row("Interpass Max.:", v(ph.interpassMax))}
  </div>`;

  const right = `<div class="col">
    ${jointSketch(dd.awsJointDetail || j.type || doc.wpsNumber)}
    ${sect("Position")}
    ${row("Groove:", v(pos.groove))}
    ${row("Fillet:", v(pos.fillet))}
    ${row("Progression:", bf(pos.progression,"Up") + "&nbsp;&nbsp;" + bf(pos.progression,"Down"))}
    ${sect("Electrical Characteristics")}
    ${row("Transfer Mode:", bf(el.transferMode,"Short")+" "+bf(el.transferMode,"Globular")+" "+bf(el.transferMode,"Spray")+" "+bf(el.transferMode,"Pulsed"))}
    ${row("Current:", bf(el.current,"AC")+" "+bf(el.current,"DCEP")+" "+bf(el.current,"DCEN")+" "+bf(el.current,"Pulsed"))}
    ${row("Other:", v(el.other))}
    ${row("Tungsten Size:", v(el.tungstenSize))}
    ${row("Tungsten Type:", v(el.tungstenType))}
    ${sect("Technique")}
    ${row("Stringer/Weave:", v(tc.stringerWeave))}
    ${row("Multi/Single Pass:", v(tc.multiSinglePass))}
    ${row("# Electrodes:", v(tc.numElectrodes))}
    ${row("CTWD:", v(tc.ctwd))}
    ${row("Angle:", v(tc.angle))}
    ${row("Peening:", v(tc.peening))}
    ${row("Interpass Clean:", v(tc.interpassClean))}
    ${sect("PWHT")}
    ${row("Required:", yn(pw.required))}
    ${row("Temp. (°F):", v(pw.temp))}
    ${row("Time:", v(pw.time))}
  </div>`;

  const proc = `<div class="proc-head"><span class="t">Welding Procedure</span><span class="hi">Heat Input (calc. §5.5.3): ${v(pr.heatInput)}</span></div>
    <table class="proc"><thead><tr>
      <th>Layer/Pass</th><th>Process</th><th>Filler Class</th><th>Dia.</th><th>Cur. Type</th><th>Amps/WFS</th><th>Volts</th><th>Travel IPM</th><th>Notes</th>
    </tr></thead><tbody>
      <tr><td>${v(pr.layerPass || "All")}</td><td>${v(pr.process || doc.process)}</td><td>${v(pr.fillerClass || awsClass)}</td><td>${v(pr.dia)}</td><td>${v(pr.curType || el.current)}</td><td>${v(pr.amps)}</td><td>${v(pr.volts)}</td><td>${v(pr.travel)}</td><td>${dd.notes ? esc(dd.notes) : "N/A"}</td></tr>
    </tbody></table>`;

  const body = `${meta}<div class="cols">${left}${right}</div>${proc}`;
  openPrint(shell({
    title: "WPS No.", subtitle: "Welding Procedure Specification (WPS) · AWS D1.1",
    docNo: doc.wpsNumber, rev: doc.revision, body,
    footerLeft: "Last Modified: " + fmtDate(doc.updatedAt || doc.wpsDate),
  }));
}

export function printPQR(doc) {
  const dd = doc.documentData || {};
  const j = dd.joint || {}, fl = dd.filler || {}, el = dd.electrical || {};
  const th = dd.thermal || {}, te = dd.tensile || {}, be = dd.bend || {}, ex = dd.exam || {};
  const pos = dd.position || dd.positionOfGroove || "";
  const awsClass = fl.awsClass || dd.fillerMetal || "";

  const meta = `<table class="meta"><tbody>
    <tr>${metaCell("PQR No.", v(doc.pqrNumber))}${metaCell("Revision", v(doc.revision))}${metaCell("Date", fmtDate(doc.pqrDate))}${metaCell("Supporting WPS", v(doc.wpsNumber))}</tr>
    <tr>${metaCell("Prepared By", v(doc.preparedBy))}${metaCell("Authorized By", v(doc.authorizedBy))}${metaCell("Welding Process(es)", v(doc.process))}${metaCell("Position", v(pos))}</tr>
  </tbody></table>`;

  const left = `<div class="col">
    ${sect("Joint Design")}
    ${row("Type:", v(j.type))}
    ${row("Backing:", yn(j.backing))}
    ${row("Root Opening:", v(j.rootOpening))}
    ${row("Groove Angle:", v(j.grooveAngle))}
    ${sect("Base Metals")}
    ${row("Spec.:", v(dd.baseMetalSpec))}
    ${row("Thickness:", v(dd.baseMetalThickness))}
    ${sect("Filler Metals")}
    ${row("AWS Spec.:", v(fl.awsSpec))}
    ${row("AWS Class.:", v(awsClass))}
    ${row("SFA Spec.:", v(fl.sfaSpec))}
    ${sect("Thermal Controls")}
    ${row("Preheat (°F):", v(th.preheat))}
    ${row("Interpass Min.:", v(th.interpassMin))}
    ${row("Interpass Max.:", v(th.interpassMax))}
    ${row("PWHT:", v(th.pwht))}
  </div>`;

  const right = `<div class="col">
    ${jointSketch(j.type || doc.pqrNumber)}
    ${sect("Electrical (As-Tested)")}
    ${row("Current / Polarity:", v(el.currentPolarity))}
    ${row("Amps / WFS:", v(el.amps))}
    ${row("Volts:", v(el.volts))}
    ${row("Travel IPM:", v(el.travelIPM))}
    ${row("Heat Input:", v(el.heatInput))}
    ${sect("Tensile Test")}
    ${row("Specimen:", v(te.specimen))}
    ${row("Ult. Load:", v(te.ultLoad))}
    ${row("Ult. Strength:", v(te.ultStrength))}
    ${row("Failure Location:", v(te.failureLoc))}
    ${sect("Guided Bend")}
    ${row("Type:", v(be.type))}
    ${row("Result:", v(be.result))}
    ${sect("Examination")}
    ${row("Visual:", v(ex.visual))}
    ${row("RT Result:", v(ex.rtResult))}
  </div>`;

  const results = `<div class="proc-head"><span class="t">Test Results (D1.1 Clause 4 — Procedure Qualification)</span></div>
    <div class="full">${dd.testResultsSummary ? esc(dd.testResultsSummary) : "No test results recorded."}</div>
    ${ex.remarks ? `<div class="sh">Remarks</div><div class="full">${esc(ex.remarks)}</div>` : ""}
    ${dd.notes ? `<div class="sh">Notes</div><div class="full">${esc(dd.notes)}</div>` : ""}`;

  const body = `${meta}<div class="cols">${left}${right}</div>${results}`;
  openPrint(shell({
    title: "PQR No.", subtitle: "Procedure Qualification Record (PQR) · AWS D1.1",
    docNo: doc.pqrNumber, rev: doc.revision, body,
    footerLeft: "Last Modified: " + fmtDate(doc.updatedAt || doc.pqrDate),
  }));
}

export function printWQTR(doc) {
  const dd = doc.documentData || {};
  const vr = dd.variables || {}, rg = dd.ranges || {}, rs = dd.results || {};

  const meta = `<table class="meta"><tbody>
    <tr>${metaCell("WQTR No.", v(doc.wqtrNumber))}${metaCell("Revision", v(doc.revision))}${metaCell("Test Date", fmtDate(doc.wqtrDate))}${metaCell("Welder Name", v(doc.welderName))}</tr>
    <tr>${metaCell("Welder Stamp/ID", v(doc.welderStamp))}${metaCell("Authorized By", v(doc.authorizedBy))}${metaCell("Welding Process(es)", v(dd.process || doc.process))}${metaCell("Position", v(vr.position))}</tr>
  </tbody></table>`;

  const left = `<div class="col">
    ${sect("Qualified Variables")}
    ${row("Process:", v(dd.process || doc.process))}
    ${row("Transfer Mode:", v(vr.transferMode))}
    ${row("Electrodes:", v(vr.electrodes))}
    ${row("Current:", v(vr.current))}
    ${row("Position:", v(vr.position))}
    ${row("Backing:", yn(vr.backing))}
    ${row("Insert:", v(vr.insert))}
    ${sect("Qualified Ranges")}
    ${row("Thickness Qualified:", v(rg.thicknessQualified))}
    ${row("Diameter Qualified:", v(rg.diameterQualified))}
    ${row("Position Qualified:", v(rg.positionQualified))}
    ${row("Progression:", v(rg.progression))}
  </div>`;

  const right = `<div class="col">
    ${sect("Material & Consumables")}
    ${row("Material (From):", v(vr.materialFrom))}
    ${row("Material (To):", v(vr.materialTo))}
    ${row("Plate Dimensions:", v(vr.plateDimensions))}
    ${row("Pipe Dimensions:", v(vr.pipeDimensions))}
    ${row("Filler Metal:", v(vr.fillerMetal))}
    ${row("Shielding Gas:", v(vr.shieldingGas))}
    ${sect("Results")}
    ${row("Visual:", v(rs.visual))}
    ${row("Bend / RT Result:", v(rs.bendResult))}
    ${row("Overall Result:", v(rs.result))}
  </div>`;

  const results = `<div class="proc-head"><span class="t">Test Results (D1.1 Clause 4 — Welder Qualification)</span></div>
    <div class="full">${dd.testResults ? esc(dd.testResults) : "No test results recorded."}</div>
    ${dd.notes ? `<div class="sh">Notes</div><div class="full">${esc(dd.notes)}</div>` : ""}`;

  const body = `${meta}<div class="cols">${left}${right}</div>${results}`;
  openPrint(shell({
    title: "WQTR No.", subtitle: "Welder Qualification Test Record (WQTR) · AWS D1.1",
    docNo: doc.wqtrNumber, rev: doc.revision, body,
    footerLeft: "Last Modified: " + fmtDate(doc.updatedAt || doc.wqtrDate),
  }));
}
